"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { AlertCircle, Search, X } from "lucide-react";
import { geoService, type Prediction } from "../../services/geoService";
import { useGeo } from "@/app/context/GeoContext";
import { generateCookieId, getClientIp } from "@/app/utils/clientInfo";
import { resolveLegacyLink } from "@/app/utils/linkUtils";

export default function AddressInput() {
    const [query, setQuery] = useState("");
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const [isSelecting, setIsSelecting] = useState(false);

    // On récupère les fonctions du contexte global
    const { setAgency, setHasSelected, clearAgency } = useGeo();

    const router = useRouter();

    // Récupération des suggestions d’adresse
    useEffect(() => {
        const handler = setTimeout(async () => {
            if (isSelecting) {
                setIsSelecting(false);
                return;
            }
            if (query.length >= 3) {
                try {
                    const data = await geoService.getPredictions(query);
                    const addresses = data.filter((d: Prediction) =>
                        d.types.some((t) =>
                            ["subpremise", "street_address", "geocode"].includes(t)
                        )
                    );
                    setPredictions(addresses);
                    setShowPredictions(true);
                } catch (err) {
                    console.error("Erreur API predictions:", err);
                }
            } else {
                setPredictions([]);
                setShowPredictions(false);
            }
        }, 400);

        return () => clearTimeout(handler);
    }, [query]);

    const handleSelectPlace = async (placeId: string, description: string) => {
        setIsSelecting(true);
        setQuery(description);
        setPredictions([]);
        setShowPredictions(false);
        setError(null);
        setLoading(true);

        try {
            const agency = await geoService.getMap(placeId);
            console.log("Nouvelle agence détectée :", agency);
            // Cas: API renvoie un message (pas d'agence) au lieu d'une erreur
            const noAgencyMessage =
                typeof agency === "string"
                    ? agency
                    : agency?.message || agency?.error || agency?.data?.message;

            if (noAgencyMessage) {
                setHasSelected(false);
                setError(noAgencyMessage); // <-- affiche le message exact de l’API
                return;
            }
            //Supprimer l’ancienne avant de sauvegarder la nouvelle
            clearAgency();

            //Sauvegarde de la nouvelle agence
            setAgency({
                ...agency,
                phoneIdelio: agency.phoneIdelio ?? null,
            });
            setHasSelected(true);

            //Redirection vers la page de l’agence si le slug existe
            if (agency.url_agence) {
                router.push(resolveLegacyLink(`/portage-de-repas/${agency.url_agence}`));
            } else {
                setError("Agence introuvable pour cette adresse.");
            }


        } catch (err) {
            console.error("Erreur getmap:", err);
            setError("Impossible de vérifier l'adresse. Réessayez.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative w-full">
            {/* Champ de saisie */}
            <div className="relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    id="address-input"
                    placeholder="Saisir l'adresse de livraison"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => predictions.length > 0 && setShowPredictions(true)}
                    onBlur={() => {
                        setTimeout(() => setShowPredictions(false), 300);
                    }}
                    className={`w-full rounded-lg border pl-10 pr-10 py-2 outline-none transition-all duration-200
    hover:border-2 hover:border-primaryGreen hover:shadow-[0_15px_20px_0_rgba(0,0,0,0.15)]
    focus-visible:border-2 focus-visible:border-primaryGreen focus-visible:ring-0 
    ${error
                            ? "border-secondary"
                            : "border-primaryRed"
                        }`}
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            setPredictions([]);
                            setShowPredictions(false);
                            setError(null);
                        }}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-black"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* Liste des suggestions */}
            {showPredictions && predictions.length > 0 && (
                <ul
                    className="absolute z-50 mt-2 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {predictions.map((p) => (
                        <li
                            key={p.placeId}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowPredictions(false);
                                handleSelectPlace(p.placeId, p.description);
                            }}
                            role="button"
                            tabIndex={0}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                            {p.description}
                        </li>
                    ))}
                </ul>
            )}

            {/* Message d'erreur - POSITION ABSOLUTE pour ne pas décaler l'input */}
            {error && (
                <div className="absolute left-0 right-0 mt-2 flex items-center gap-2 rounded border px-3 py-2 text-sm border-[#FDA29B] bg-[#FEF3F2] text-text z-40">
                    <AlertCircle className="h-5 w-5 text-secondary" />
                    {error}
                </div>
            )}

            {/* Loader */}
            {loading && <p className="mt-2 text-sm text-gray-500">Recherche en cours...</p>}
        </div>
    );
}
